import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';

import { SegmentTrackEvent, ShareActions } from '@ninety/mobile/platform/services';

import { SegmentActions } from '../analytics/segment/segment.actions';

/**
 * Segment analytics for the native link-sharing funnel.
 *
 * Root-scoped rather than feature-scoped: `ShareService.shareContent` is called from every detail page
 * and the shared `nty-detail-footer`, so a feature-scoped effect would miss shares from surfaces that
 * aren't loaded.
 *
 * `entityType` is set inline because it's polymorphic — every `ShareType` is 1:1 with an `EntityType`,
 * so `shareType` is used directly.
 */
@Injectable()
export class SharingAnalyticsEffects {
  private readonly actions$ = inject(Actions);

  trackLinkShareOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareOpened),
      map(({ entityId, shareType }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINK_SHARE_OPENED,
          params: { entityId, entityType: shareType },
        })
      )
    )
  );

  trackLinkShared$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareCompleted),
      map(({ entityId, shareType, targetApp }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINK_SHARED,
          params: { entityId, entityType: shareType, targetApp },
        })
      )
    )
  );

  trackLinkShareAbandoned$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareAbandoned),
      map(({ entityId, shareType }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINK_SHARE_ABANDONED,
          params: { entityId, entityType: shareType },
        })
      )
    )
  );

  trackLinkShareFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareActions.shareFailed),
      map(({ entityId, shareType, reason }) =>
        SegmentActions.track({
          event: SegmentTrackEvent.LINK_SHARE_FAILED,
          params: { entityId, entityType: shareType, reason },
        })
      )
    )
  );
}
